import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useState, useEffect } from "react";
import theme from "../../theme";
import useButtonStyles from "../../styles/useButtonStyles";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { Link, useNavigate } from "react-router-dom";
import SelectInputField from "../forms/formElements/SelectInputField";
import { APIConstants } from "../../constants/ApiConstants";
import { constant } from "../../constants";
import axios from "axios";
import BasicModal from "../ui/BasicModal";
import Loader from "../ui/Loader";
import BasicTextField from "../forms/formElements/BasicTextField";
import { AddAdminData } from "../../static-models/AdminFormData";
import { useAppSelector } from "../../redux/hooks";
import { useDispatch } from "react-redux";
import { roleActions } from "../../redux/slices/roleSlice";
import CloseIcon from "@mui/icons-material/Close";
import { filterConstants } from "../../constants/AppConstants";

export interface IAppProps {}
export interface AddAdminFormProps {
  initialFormData: AddAdminFormDataType;
  closeAddUser?: Function;
  fetchAdminList?: Function;
}
export interface AddAdminFormDataType {
  firstName: string;
  lastName: string;
  department: string;
  email: string;
  password: string;
  status: string;
  user_role: string;
}

export function AddUser(props: AddAdminFormProps) {
  const { initialFormData, closeAddUser, fetchAdminList } = props;

  const [showModal, setShowModal] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<boolean>(false);
  const [roles, setRoles] = useState<string[]>([]);

  const [adminFormState, setAdminFormState] =
    useState<AddAdminFormDataType>(initialFormData);
  const [loading, setLoading] = useState<boolean>(false);
  const [clearForm, setClearForm] = useState(false);
  const [disableSubmitBtn, setDisableSubmitBtn] = useState(true);
  const [errorMes, setErrorMes] = useState("Something went wrong");
  const buttonStyles = useButtonStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const submitHandler = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    await createAdminUser();
  };
  const roleList = useAppSelector(
    (state: any) => state.persistReducers.getRoleList.roleList
  );

  useEffect(() => {
    setDisableSubmitBtn(!isValidForm());
  }, [adminFormState]);

  useEffect(() => {
    const fetchRoleList = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          process.env.REACT_APP_API_URL + APIConstants.role + "?type=Admin"
        );
        const data = response.data.data;

        const roleListData = data.map((item: any) => {
          return {
            name: item.name ? item.name : "-",
            description: item.description ? item.description : "-",
          };
        });

        const roles = data.map((item: any) => {
          return item.name;
        });
        setRoles(roles);
        dispatch(roleActions.updateRoleList({ roleList: roleListData }));
        setLoading(false);
        setUploadError(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
        setUploadError(false);
      }
    };

    fetchRoleList();
  }, []);

  const isValidForm = () => {
    const isNameValid =
      adminFormState.firstName.trim() !== "" &&
      adminFormState.lastName.trim() !== "";
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    const isValidEmail = emailRegex.test(adminFormState.email);
    const isStatusValid = adminFormState.status.trim() !== "";
    const isRoleValid = adminFormState.user_role.trim() !== "";
    return isNameValid && isValidEmail && isStatusValid && isRoleValid;
  };

  const createAdminUser = async () => {
    setLoading(true);
    const payload: any = adminFormState;
    try {
      const axiosResponse = await axios.post(
        process.env.REACT_APP_API_URL + APIConstants.admin,
        payload
      );
      setUploadError(false);
      fetchAdminList && fetchAdminList();
    } catch (error: any) {
      console.error(error);
      setShowModal(true);
      setUploadError(true);
      setErrorMes(error?.response?.data?.message);
    } finally {
      setLoading(false);
      setShowModal(true);
    }
  };
  const onCloseUser = () => {
    closeAddUser && closeAddUser(false);
  };
  return (
    <Grid display={"flex"} flexDirection={"column"} height={"100%"}>
      <Grid
        item
        width={"100%"}
        height={"6.25rem"}
        display={"flex"}
        alignItems={"center"}
        sx={{ background: theme.color?.background }}
      >
        <Grid
          item
          display={"flex"}
          alignItems={"center"}
          justifyContent={"space-between"}
          px={4}
          gap={2}
          width={"100%"}
        >
          <Typography
            sx={{
              fontFamily: theme.font?.fontFamily.primary,
              fontSize: theme.font?.fontSize.large,
              fontWeight: theme.font?.fontWeight.thick,
            }}
            pl={2}
          >
            Add User
          </Typography>
          <IconButton
            aria-label="delete"
            onClick={closeAddUser ? () => closeAddUser(false) : () => {}}
          >
            <CloseIcon />
          </IconButton>
        </Grid>
      </Grid>

      <Grid
        sx={{
          backgroundColor: theme?.color?.background,
        }}
        component={"form"}
        onSubmit={submitHandler}
        alignSelf={"center"}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
        width={"100%"}
        height={"100%"}
      >
        <Grid
          container
          display={"flex"}
          flexDirection={"column"}
          width={"100%"}
          marginBottom={2}
          gap={2}
          pt={2}
          px={4}
        >
          <Grid display={"flex"} gap={2}>
            <Grid item width={"50%"}>
              <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                First Name *
              </Typography>
              <BasicTextField
                placeholder=""
                type="text"
                stateKey="firstName"
                defaultValue={adminFormState.firstName}
                setState={setAdminFormState}
                required={true}
              ></BasicTextField>
            </Grid>
            <Grid item width={"50%"}>
              <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                Last Name *
              </Typography>
              <BasicTextField
                placeholder=""
                type="text"
                stateKey="lastName"
                setState={setAdminFormState}
                defaultValue={adminFormState.lastName}
                required={true}
              />
            </Grid>
          </Grid>
          <Grid display={"flex"} gap={2}>
            <Grid item width={"50%"}>
              <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                Email *
              </Typography>
              <BasicTextField
                placeholder=""
                stateKey="email"
                type="email"
                defaultValue={adminFormState.email}
                setState={setAdminFormState}
                required={true}
                disableSubmit={() => {}}
              />
            </Grid>
            <Grid item width={"50%"}>
              <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                User Role *
              </Typography>
              <SelectInputField
                sx={{ width: "100%" }}
                setState={setAdminFormState}
                stateKey="user_role"
                align="left"
                options={roles}
                defaultValue={
                  adminFormState.user_role ? adminFormState.user_role : ""
                }
                loading={loading}
              />
            </Grid>
          </Grid>
          <Grid display={"flex"} gap={2}>
            <Grid item width={"50%"}>
              <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                Department
              </Typography>
              <BasicTextField
                placeholder=""
                type="text"
                stateKey="department"
                defaultValue={adminFormState.department}
                setState={setAdminFormState}
              />
            </Grid>
            <Grid item width={"50%"}>
              <Typography sx={{ fontFamily: theme.font?.fontFamily.primary }}>
                Status *
              </Typography>
              <SelectInputField
                sx={{ width: "100%" }}
                setState={setAdminFormState}
                stateKey="status"
                defaultValue={
                  adminFormState.status ? adminFormState.status : ""
                }
                align="left"
                options={[filterConstants.Active, filterConstants.Inactive]}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          marginTop={4}
          item
          justifyContent={"center"}
          padding={2}
          pt={3}
          pb={4}
          borderTop={`${theme?.border?.xsmall}${theme.color?.dividerLight}`}
          display={"flex"}
          gap={2}
        >
          <Button
            sx={{ ...buttonStyles.outlinedSecondary, width: "45%" }}
            variant="outlined"
            onClick={closeAddUser ? () => closeAddUser(false) : () => {}}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            sx={{ ...buttonStyles.containedSecondary, width: "45%" }}
            variant="contained"
            disabled={disableSubmitBtn}
          >
            {loading ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </Grid>
      </Grid>
      {showModal &&
        (uploadError ? (
          <BasicModal
            title={errorMes ? errorMes : "Something went wrong"}
            success={false}
            setState={setShowModal}
            onCloseModule={onCloseUser}
          />
        ) : (
          <BasicModal
            title={"Admin Added Successfully!"}
            success={true}
            setState={setShowModal}
            onCloseModule={onCloseUser}
          />
        ))}
    </Grid>
  );
}
